import { createId, db, emitDbChange, makeBase, nowIso, queueMutation, saveEntity, todayKey } from "./db";
import { MEDICATION_LABELS, normalizeMedicationKind } from "./health";
import type {
  Client,
  ClientRate,
  Coordinates,
  FoodItem,
  FoodLog,
  FoodMeal,
  InvoiceSummary,
  LocationRule,
  MealPreset,
  MedicationKind,
  MedicationLog,
  MealType,
  Project,
  SymptomKind,
  SymptomLog,
  TrackerSnapshot,
  VoiceNote,
  WorkSession
} from "./types";

export function active<T extends { deleted_at?: string | null }>(items: T[]): T[] {
  return items.filter((item) => !item.deleted_at);
}

export async function loadSnapshot(): Promise<TrackerSnapshot> {
  const [
    clients,
    client_rates,
    projects,
    work_sessions,
    food_items,
    food_logs,
    meal_presets,
    medication_logs,
    symptom_logs,
    location_rules,
    voice_notes,
    transcription_jobs
  ] = await Promise.all([
    db.clients.toArray(),
    db.client_rates.toArray(),
    db.projects.toArray(),
    db.work_sessions.toArray(),
    db.food_items.toArray(),
    db.food_logs.toArray(),
    db.meal_presets.toArray(),
    db.medication_logs.toArray(),
    db.symptom_logs.toArray(),
    db.location_rules.toArray(),
    db.voice_notes.toArray(),
    db.transcription_jobs.toArray()
  ]);

  return {
    clients,
    client_rates,
    projects,
    work_sessions,
    food_items,
    food_logs,
    meal_presets,
    medication_logs,
    symptom_logs,
    location_rules,
    voice_notes,
    transcription_jobs
  };
}

export async function ensureClient(name: string): Promise<Client> {
  const normalized = normalizeName(name);
  const existing = await db.clients
    .where("name")
    .equalsIgnoreCase(normalized)
    .filter((client) => !client.deleted_at)
    .first();
  if (existing) {
    return existing;
  }

  const client: Client = {
    ...makeBase(),
    name: normalized,
    hourly_rate_cents: 0,
    currency: "EUR",
    billing_increment_minutes: 15
  };
  await saveEntity("clients", client);
  return client;
}

export async function renameClient(client: Client, name: string): Promise<Client> {
  const normalized = normalizeName(name);
  if (!normalized) {
    throw new Error("Il nome del cliente non puo essere vuoto.");
  }
  const duplicate = await db.clients
    .where("name")
    .equalsIgnoreCase(normalized)
    .filter((item) => !item.deleted_at && item.id !== client.id)
    .first();
  if (duplicate) {
    throw new Error("Esiste gia un cliente con questo nome. Usa Unisci.");
  }

  const current = await db.clients.get(client.id);
  if (!current || current.deleted_at) {
    throw new Error("Cliente non trovato.");
  }
  const updated: Client = {
    ...current,
    name: normalized,
    updated_at: nowIso(),
    sync_status: "pending"
  };
  await saveEntity("clients", updated);
  return updated;
}

export async function deleteClient(client: Client): Promise<Client> {
  const current = await db.clients.get(client.id);
  if (!current || current.deleted_at) {
    throw new Error("Cliente non trovato.");
  }
  const sessions = await db.work_sessions
    .where("client_id")
    .equals(client.id)
    .filter((session) => !session.deleted_at)
    .count();
  if (sessions > 0) {
    throw new Error(
      `Il cliente ha ${sessions} ${sessions === 1 ? "sessione" : "sessioni"}. Uniscilo a un altro cliente invece di eliminarlo.`
    );
  }

  const timestamp = nowIso();
  const projects = await db.projects
    .where("client_id")
    .equals(client.id)
    .filter((project) => !project.deleted_at)
    .toArray();
  const rates = await db.client_rates
    .where("client_id")
    .equals(client.id)
    .filter((rate) => !rate.deleted_at)
    .toArray();
  const rules = await db.location_rules
    .where("client_id")
    .equals(client.id)
    .filter((rule) => !rule.deleted_at)
    .toArray();

  for (const project of projects) {
    await saveEntity(
      "projects",
      { ...project, deleted_at: timestamp, updated_at: timestamp, sync_status: "pending" },
      "delete"
    );
  }
  for (const rate of rates) {
    await saveEntity(
      "client_rates",
      { ...rate, deleted_at: timestamp, updated_at: timestamp, sync_status: "pending" },
      "delete"
    );
  }
  for (const rule of rules) {
    await saveEntity(
      "location_rules",
      { ...rule, deleted_at: timestamp, updated_at: timestamp, sync_status: "pending" },
      "delete"
    );
  }

  const deleted: Client = {
    ...current,
    deleted_at: timestamp,
    updated_at: timestamp,
    sync_status: "pending"
  };
  await saveEntity("clients", deleted, "delete");
  return deleted;
}

export async function updateClientBilling(
  client: Client,
  hourlyRateEuro: number,
  billingIncrementMinutes: number
): Promise<Client> {
  const updated: Client = {
    ...client,
    hourly_rate_cents: Math.max(0, Math.round(hourlyRateEuro * 100)),
    billing_increment_minutes: Math.max(1, Math.round(billingIncrementMinutes)),
    updated_at: nowIso(),
    sync_status: "pending"
  };
  await saveEntity("clients", updated);
  return updated;
}

export async function clearClientLegacyRate(client: Client): Promise<Client> {
  return updateClientBilling(client, 0, client.billing_increment_minutes);
}

export async function updateClientBillingIncrement(clientId: string, billingIncrementMinutes: number): Promise<Client> {
  const client = await db.clients.get(clientId);
  if (!client || client.deleted_at) {
    throw new Error("Cliente non trovato.");
  }
  return updateClientBilling(client, client.hourly_rate_cents / 100, billingIncrementMinutes);
}

export function effectiveClientRate(
  rates: ClientRate[],
  clientId: string,
  at: string | Date
): ClientRate | undefined {
  const date = at instanceof Date ? localDateKey(at) : /^\d{4}-\d{2}-\d{2}$/.test(at) ? at : localDateKey(at);
  return active(rates)
    .filter((rate) => rate.client_id === clientId && rate.valid_from <= date)
    .sort((a, b) => b.valid_from.localeCompare(a.valid_from) || b.updated_at.localeCompare(a.updated_at))[0];
}

export async function saveClientRate(
  client: Client,
  hourlyRateEuro: number,
  validFrom: string
): Promise<ClientRate> {
  if (!Number.isFinite(hourlyRateEuro) || hourlyRateEuro < 0) {
    throw new Error("Tariffa non valida.");
  }
  if (!isDateKey(validFrom)) {
    throw new Error("Data di decorrenza non valida.");
  }

  const timestamp = nowIso();
  const existing = await db.client_rates
    .where("client_id")
    .equals(client.id)
    .filter((rate) => !rate.deleted_at && rate.valid_from === validFrom)
    .first();
  const rate: ClientRate = existing
    ? {
        ...existing,
        hourly_rate_cents: Math.round(hourlyRateEuro * 100),
        currency: client.currency,
        updated_at: timestamp,
        sync_status: "pending"
      }
    : {
        ...makeBase(),
        client_id: client.id,
        valid_from: validFrom,
        hourly_rate_cents: Math.round(hourlyRateEuro * 100),
        currency: client.currency
      };
  await saveEntity("client_rates", rate);
  return rate;
}

export async function deleteClientRate(rate: ClientRate): Promise<ClientRate> {
  const timestamp = nowIso();
  const deleted: ClientRate = {
    ...rate,
    deleted_at: timestamp,
    updated_at: timestamp,
    sync_status: "pending"
  };
  await saveEntity("client_rates", deleted, "delete");
  return deleted;
}

export async function repriceClientWorkSessions(clientId: string): Promise<number> {
  const client = await db.clients.get(clientId);
  if (!client || client.deleted_at) {
    throw new Error("Cliente non trovato.");
  }
  const rates = active(await db.client_rates.where("client_id").equals(clientId).toArray());
  const sessions = await db.work_sessions
    .where("client_id")
    .equals(clientId)
    .filter((session) => !session.deleted_at && Boolean(session.ended_at))
    .toArray();
  let changed = 0;

  for (const session of sessions) {
    const rate = effectiveClientRate(rates, clientId, session.started_at);
    const hourlyRateCents = rate?.hourly_rate_cents ?? client.hourly_rate_cents;
    const currency = rate?.currency ?? client.currency;
    if (
      session.hourly_rate_cents === hourlyRateCents &&
      session.currency === currency
    ) {
      continue;
    }
    await saveEntity("work_sessions", {
      ...session,
      hourly_rate_cents: hourlyRateCents,
      currency,
      updated_at: nowIso(),
      sync_status: "pending"
    });
    changed += 1;
  }
  return changed;
}

export async function ensureProject(clientId: string, name: string): Promise<Project> {
  const normalized = normalizeName(name || "Generale");
  const existing = await db.projects
    .where("client_id")
    .equals(clientId)
    .filter((project) => !project.deleted_at && project.name.toLowerCase() === normalized.toLowerCase())
    .first();
  if (existing) {
    return existing;
  }

  const project: Project = {
    ...makeBase(),
    client_id: clientId,
    name: normalized,
    archived: false
  };
  await saveEntity("projects", project);
  return project;
}

export async function renameProject(project: Project, name: string): Promise<Project> {
  const normalized = normalizeName(name);
  if (!normalized) {
    throw new Error("Il nome del progetto non puo essere vuoto.");
  }
  const duplicate = await db.projects
    .where("client_id")
    .equals(project.client_id)
    .filter(
      (item) =>
        !item.deleted_at &&
        item.id !== project.id &&
        item.name.toLocaleLowerCase() === normalized.toLocaleLowerCase()
    )
    .first();
  if (duplicate) {
    throw new Error("Esiste gia un progetto con questo nome per il cliente.");
  }

  const current = await db.projects.get(project.id);
  if (!current || current.deleted_at) {
    throw new Error("Progetto non trovato.");
  }
  const updated: Project = {
    ...current,
    name: normalized,
    updated_at: nowIso(),
    sync_status: "pending"
  };
  await saveEntity("projects", updated);
  return updated;
}

export async function deleteProject(project: Project): Promise<Project> {
  const current = await db.projects.get(project.id);
  if (!current || current.deleted_at) {
    throw new Error("Progetto non trovato.");
  }
  const sessions = await db.work_sessions
    .where("project_id")
    .equals(project.id)
    .filter((session) => !session.deleted_at)
    .count();
  if (sessions > 0) {
    throw new Error(
      `Il progetto ha ${sessions} ${sessions === 1 ? "sessione" : "sessioni"} e non puo essere eliminato.`
    );
  }

  const timestamp = nowIso();
  const deleted: Project = {
    ...current,
    deleted_at: timestamp,
    updated_at: timestamp,
    sync_status: "pending"
  };
  await saveEntity("projects", deleted, "delete");
  return deleted;
}

export interface ClientMergeResult {
  sessions: number;
  projectsMoved: number;
  projectsMerged: number;
  ratesMoved: number;
  ratesMerged: number;
  locationRules: number;
}

export async function mergeClients(sourceClient: Client, targetClient: Client): Promise<ClientMergeResult> {
  if (sourceClient.id === targetClient.id) {
    throw new Error("Scegli due clienti diversi.");
  }
  const [source, target] = await Promise.all([
    db.clients.get(sourceClient.id),
    db.clients.get(targetClient.id)
  ]);
  if (!source || source.deleted_at || !target || target.deleted_at) {
    throw new Error("Uno dei clienti non esiste piu.");
  }

  const result: ClientMergeResult = {
    sessions: 0,
    projectsMoved: 0,
    projectsMerged: 0,
    ratesMoved: 0,
    ratesMerged: 0,
    locationRules: 0
  };
  const timestamp = nowIso();

  if (target.hourly_rate_cents === 0 && source.hourly_rate_cents > 0) {
    await saveEntity("clients", {
      ...target,
      hourly_rate_cents: source.hourly_rate_cents,
      currency: source.currency,
      updated_at: timestamp,
      sync_status: "pending"
    });
  }

  const [targetProjects, sourceProjects] = await Promise.all([
    db.projects
      .where("client_id")
      .equals(target.id)
      .filter((project) => !project.deleted_at)
      .toArray(),
    db.projects
      .where("client_id")
      .equals(source.id)
      .filter((project) => !project.deleted_at)
      .toArray()
  ]);
  const targetProjectsByName = new Map(
    targetProjects.map((project) => [normalizeName(project.name).toLocaleLowerCase(), project])
  );
  const projectIds = new Map<string, string>();
  for (const project of sourceProjects) {
    const duplicate = targetProjectsByName.get(normalizeName(project.name).toLocaleLowerCase());
    if (duplicate) {
      projectIds.set(project.id, duplicate.id);
      await saveEntity(
        "projects",
        { ...project, deleted_at: timestamp, updated_at: timestamp, sync_status: "pending" },
        "delete"
      );
      result.projectsMerged += 1;
      continue;
    }

    const moved: Project = {
      ...project,
      client_id: target.id,
      updated_at: timestamp,
      sync_status: "pending"
    };
    await saveEntity("projects", moved);
    projectIds.set(project.id, project.id);
    targetProjectsByName.set(normalizeName(project.name).toLocaleLowerCase(), moved);
    result.projectsMoved += 1;
  }

  const [targetRates, sourceRates] = await Promise.all([
    db.client_rates
      .where("client_id")
      .equals(target.id)
      .filter((rate) => !rate.deleted_at)
      .toArray(),
    db.client_rates
      .where("client_id")
      .equals(source.id)
      .filter((rate) => !rate.deleted_at)
      .toArray()
  ]);
  const targetRatesByDate = new Map(targetRates.map((rate) => [rate.valid_from, rate]));
  for (const rate of sourceRates) {
    if (targetRatesByDate.has(rate.valid_from)) {
      await saveEntity(
        "client_rates",
        { ...rate, deleted_at: timestamp, updated_at: timestamp, sync_status: "pending" },
        "delete"
      );
      result.ratesMerged += 1;
      continue;
    }
    const moved: ClientRate = {
      ...rate,
      client_id: target.id,
      updated_at: timestamp,
      sync_status: "pending"
    };
    await saveEntity("client_rates", moved);
    targetRatesByDate.set(rate.valid_from, moved);
    result.ratesMoved += 1;
  }

  const rules = await db.location_rules
    .where("client_id")
    .equals(source.id)
    .filter((rule) => !rule.deleted_at)
    .toArray();
  for (const rule of rules) {
    await saveEntity("location_rules", {
      ...rule,
      client_id: target.id,
      updated_at: timestamp,
      sync_status: "pending"
    });
    result.locationRules += 1;
  }

  const sessions = await db.work_sessions
    .where("client_id")
    .equals(source.id)
    .filter((session) => !session.deleted_at)
    .toArray();
  for (const session of sessions) {
    await updateWorkSession(session, {
      client_id: target.id,
      project_id: projectIds.get(session.project_id) ?? session.project_id
    });
    result.sessions += 1;
  }

  await saveEntity(
    "clients",
    { ...source, deleted_at: timestamp, updated_at: timestamp, sync_status: "pending" },
    "delete"
  );
  return result;
}

export async function getActiveWorkSession(): Promise<WorkSession | undefined> {
  return db.work_sessions
    .filter((session) => !session.deleted_at && !session.ended_at)
    .first();
}

export async function startWorkSession(clientName: string, projectName: string, notes: string): Promise<WorkSession> {
  const running = await getActiveWorkSession();
  if (running) {
    throw new Error("C'e gia un timer attivo.");
  }

  const client = await ensureClient(clientName);
  const project = await ensureProject(client.id, projectName);
  const billing = await billingForSession(client, nowIso());
  const session: WorkSession = {
    ...makeBase(),
    client_id: client.id,
    project_id: project.id,
    started_at: billing.started_at,
    ended_at: null,
    notes,
    billable: true,
    hourly_rate_cents: billing.hourly_rate_cents,
    currency: billing.currency,
    location_rule_id: null
  };
  await saveEntity("work_sessions", session);
  return session;
}

export async function stopWorkSession(session: WorkSession, notes: string): Promise<WorkSession> {
  const stoppedAt = nowIso();
  const client = await db.clients.get(session.client_id);
  const billing = client ? await billingForSession(client, session.started_at) : undefined;
  const updated: WorkSession = {
    ...session,
    ended_at: roundSessionEndToMinute(session.started_at, stoppedAt),
    notes: notes.trim() || session.notes,
    hourly_rate_cents: billing?.hourly_rate_cents ?? session.hourly_rate_cents,
    currency: billing?.currency ?? session.currency,
    updated_at: stoppedAt,
    sync_status: "pending"
  };
  await saveEntity("work_sessions", updated);
  return updated;
}

export async function switchWorkSession(
  session: WorkSession,
  clientName: string,
  projectName: string,
  notes: string
): Promise<{ closed: WorkSession | null; started: WorkSession }> {
  const running = await getActiveWorkSession();
  if (!running || running.id !== session.id) {
    throw new Error("Il timer attivo e cambiato. Riprova.");
  }

  const client = await ensureClient(clientName);
  const project = await ensureProject(client.id, projectName);
  if (running.client_id === client.id && running.project_id === project.id) {
    return { closed: null, started: running };
  }

  const switchedAt = nowIso();
  const endedAt = roundSessionEndToMinute(running.started_at, switchedAt);
  const runningClient = await db.clients.get(running.client_id);
  const runningBilling = runningClient ? await billingForSession(runningClient, running.started_at) : undefined;
  const nextBilling = await billingForSession(client, endedAt);
  const closed: WorkSession = {
    ...running,
    ended_at: endedAt,
    notes: notes.trim() || running.notes,
    hourly_rate_cents: runningBilling?.hourly_rate_cents ?? running.hourly_rate_cents,
    currency: runningBilling?.currency ?? running.currency,
    updated_at: switchedAt,
    sync_status: "pending"
  };
  await saveEntity("work_sessions", closed);

  const started: WorkSession = {
    ...makeBase(),
    client_id: client.id,
    project_id: project.id,
    started_at: nextBilling.started_at,
    ended_at: null,
    notes: "",
    billable: true,
    hourly_rate_cents: nextBilling.hourly_rate_cents,
    currency: nextBilling.currency,
    location_rule_id: null
  };
  await saveEntity("work_sessions", started);
  return { closed, started };
}

export async function updateWorkSession(session: WorkSession, updates: Partial<WorkSession>): Promise<WorkSession> {
  const clientId = updates.client_id ?? session.client_id;
  const startedAt = updates.started_at ?? session.started_at;
  const shouldRefreshBilling = updates.client_id !== undefined || updates.started_at !== undefined;
  const client = shouldRefreshBilling ? await db.clients.get(clientId) : undefined;
  const billing = client ? await billingForSession(client, startedAt) : undefined;
  const updated: WorkSession = {
    ...session,
    ...updates,
    ...(billing
      ? {
          hourly_rate_cents: billing.hourly_rate_cents,
          currency: billing.currency
        }
      : {}),
    updated_at: nowIso(),
    sync_status: "pending"
  };
  await saveEntity("work_sessions", updated);
  return updated;
}

export interface ClockTime {
  hours: number;
  minutes: number;
}

export function parseDateInput(value: string): string | undefined {
  const normalized = value.trim();
  if (isDateKey(normalized)) {
    return normalized;
  }
  const match = normalized.match(/^(\d{1,2})[./-](\d{1,2})[./-](\d{4})$/);
  if (!match) {
    return undefined;
  }
  const dateKey = `${match[3]}-${match[2].padStart(2, "0")}-${match[1].padStart(2, "0")}`;
  return isDateKey(dateKey) ? dateKey : undefined;
}

export function formatDateInput(value: string): string {
  if (!isDateKey(value)) {
    return value;
  }
  const [year, month, day] = value.split("-");
  return `${day}/${month}/${year}`;
}

export function parseClockInput(value: string): ClockTime | undefined {
  const normalized = value.trim().replace(".", ":");
  if (!normalized) {
    return undefined;
  }

  let hours: number;
  let minutes: number;
  if (normalized.includes(":")) {
    const parts = normalized.split(":");
    if (parts.length !== 2 || !parts.every((part) => /^\d{1,2}$/.test(part))) {
      return undefined;
    }
    hours = Number(parts[0]);
    minutes = Number(parts[1]);
  } else {
    if (!/^\d{1,4}$/.test(normalized)) {
      return undefined;
    }
    if (normalized.length <= 2) {
      hours = Number(normalized);
      minutes = 0;
    } else {
      hours = Number(normalized.slice(0, -2));
      minutes = Number(normalized.slice(-2));
    }
  }

  if (hours > 23 || minutes > 59) {
    return undefined;
  }
  return { hours, minutes };
}

export function parseDurationInput(value: string): number | undefined {
  const normalized = value.trim().toLowerCase().replace(/\s+/g, "").replace("h", ":");
  if (!normalized) {
    return undefined;
  }

  if (normalized.includes(":")) {
    const parts = normalized.split(":");
    if (parts.length !== 2 || !parts.every((part) => /^\d{1,2}$/.test(part))) {
      return undefined;
    }
    const hours = Number(parts[0]);
    const minutes = Number(parts[1]);
    if (minutes > 59) {
      return undefined;
    }
    return hours * 60 + minutes;
  }

  if (!/^\d{1,4}$/.test(normalized)) {
    return undefined;
  }
  if (normalized.length <= 2) {
    return Number(normalized);
  }
  const hours = Number(normalized.slice(0, -2));
  const minutes = Number(normalized.slice(-2));
  if (minutes > 59) {
    return undefined;
  }
  return hours * 60 + minutes;
}

export async function updateWorkSessionStartClock(session: WorkSession, value: string): Promise<WorkSession> {
  const clock = parseClockInput(value);
  if (!clock) {
    throw new Error("Orario non valido. Usa ad esempio 730 o 18:50.");
  }

  const currentStart = new Date(session.started_at);
  const currentEnd = session.ended_at ? new Date(session.ended_at) : undefined;
  const durationMilliseconds = currentEnd ? Math.max(60_000, currentEnd.getTime() - currentStart.getTime()) : 0;
  currentStart.setHours(clock.hours, clock.minutes, 0, 0);
  if (!currentEnd && currentStart.getTime() > Date.now()) {
    throw new Error("L'inizio del timer non può essere nel futuro.");
  }

  return updateWorkSession(session, {
    started_at: currentStart.toISOString(),
    ended_at: currentEnd ? new Date(currentStart.getTime() + durationMilliseconds).toISOString() : null
  });
}

export async function updateWorkSessionEndClock(session: WorkSession, value: string): Promise<WorkSession> {
  const clock = parseClockInput(value);
  if (!clock) {
    throw new Error("Orario non valido. Usa ad esempio 1850 o 18:50.");
  }

  const start = new Date(session.started_at);
  const end = new Date(session.ended_at ?? session.started_at);
  end.setHours(clock.hours, clock.minutes, start.getSeconds(), start.getMilliseconds());
  if (end <= start) {
    throw new Error("La fine deve essere successiva all'inizio.");
  }
  return updateWorkSession(session, { ended_at: end.toISOString() });
}

export async function updateWorkSessionDuration(session: WorkSession, value: string): Promise<WorkSession> {
  const durationMinutes = parseDurationInput(value);
  if (!durationMinutes) {
    throw new Error("Durata non valida. Usa ad esempio 130 o 25.");
  }
  const start = new Date(session.started_at);
  return updateWorkSession(session, {
    ended_at: new Date(start.getTime() + durationMinutes * 60_000).toISOString()
  });
}

export async function deleteWorkSession(session: WorkSession): Promise<WorkSession> {
  const timestamp = nowIso();
  const deleted: WorkSession = {
    ...session,
    deleted_at: timestamp,
    updated_at: timestamp,
    sync_status: "pending"
  };
  await saveEntity("work_sessions", deleted, "delete");
  return deleted;
}

export interface FoodMealTarget {
  id?: string;
  eaten_at?: string;
  meal_type?: MealType;
  notes?: string;
}

export function inferMealType(value: string | Date): MealType {
  const date = typeof value === "string" ? new Date(value) : value;
  const hour = date.getHours();
  if (hour >= 5 && hour < 11) {
    return "colazione";
  }
  if (hour >= 11 && hour < 15) {
    return "pranzo";
  }
  if (hour >= 18 && hour < 23) {
    return "cena";
  }
  return "spuntino";
}

export function effectiveMealId(log: FoodLog): string {
  if (log.meal_id) {
    return log.meal_id;
  }
  return `legacy:${localDateKey(log.eaten_at)}:${log.meal_type ?? inferMealType(log.eaten_at)}`;
}

export function groupFoodLogsIntoMeals(logs: FoodLog[]): FoodMeal[] {
  const groups = new Map<string, FoodLog[]>();
  for (const log of active(logs)) {
    const mealId = effectiveMealId(log);
    const courses = groups.get(mealId) ?? [];
    courses.push(log);
    groups.set(mealId, courses);
  }

  return [...groups.entries()]
    .map(([id, courses]) => {
      const ordered = courses.slice().sort((a, b) => a.eaten_at.localeCompare(b.eaten_at));
      const first = ordered[0];
      return {
        id,
        eaten_at: first.eaten_at,
        meal_type: first.meal_type ?? inferMealType(first.eaten_at),
        notes: ordered.find((course) => course.meal_notes?.trim())?.meal_notes?.trim() ?? "",
        courses: ordered
      };
    })
    .sort((a, b) => b.eaten_at.localeCompare(a.eaten_at));
}

export interface SuggestedMealPreset {
  name: string;
  meal_type: MealType;
  items: string[];
  support: number;
}

export function suggestMealPresets(
  logs: FoodLog[],
  foodItems: FoodItem[],
  limit = 10
): SuggestedMealPreset[] {
  const names = new Map(active(foodItems).map((item) => [item.id, item.name]));
  const meals = groupFoodLogsIntoMeals(logs)
    .map((meal) => ({
      ...meal,
      itemNames: [...new Set(meal.courses.map((course) => names.get(course.food_item_id)).filter(Boolean) as string[])]
    }))
    .filter((meal) => meal.itemNames.length >= 2);
  const candidates = new Map<
    string,
    { items: string[]; support: number; latest: string; mealTypes: Map<MealType, number> }
  >();

  for (const meal of meals) {
    const ordered = meal.itemNames.slice().sort((a, b) => a.localeCompare(b, "it", { sensitivity: "base" }));
    for (let size = 2; size <= Math.min(5, ordered.length); size += 1) {
      for (const items of combinations(ordered, size)) {
        const key = items.map((item) => item.toLocaleLowerCase("it-IT")).join("\u0000");
        const candidate = candidates.get(key) ?? {
          items,
          support: 0,
          latest: meal.eaten_at,
          mealTypes: new Map<MealType, number>()
        };
        candidate.support += 1;
        candidate.latest = candidate.latest > meal.eaten_at ? candidate.latest : meal.eaten_at;
        candidate.mealTypes.set(meal.meal_type, (candidate.mealTypes.get(meal.meal_type) ?? 0) + 1);
        candidates.set(key, candidate);
      }
    }
  }

  const ranked = [...candidates.values()]
    .filter((candidate) => candidate.support >= 2)
    .sort(
      (a, b) =>
        b.support * b.items.length - a.support * a.items.length ||
        b.items.length - a.items.length ||
        b.latest.localeCompare(a.latest)
    );
  const selected: SuggestedMealPreset[] = [];
  for (const candidate of ranked) {
    if (selected.some((item) => setSimilarity(item.items, candidate.items) > 0.72)) {
      continue;
    }
    const mealType = [...candidate.mealTypes.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] ?? "spuntino";
    selected.push({
      name: `${mealType[0].toUpperCase()}${mealType.slice(1)} frequente`,
      meal_type: mealType,
      items: candidate.items,
      support: candidate.support
    });
    if (selected.length >= limit) {
      break;
    }
  }
  return selected;
}

export async function initializeMealPresets(logs: FoodLog[], foodItems: FoodItem[]): Promise<MealPreset[]> {
  if ((await db.meal_presets.count()) > 0) {
    return [];
  }
  const suggestions = suggestMealPresets(logs, foodItems, 10);
  const presets: MealPreset[] = [];
  for (const [index, suggestion] of suggestions.entries()) {
    presets.push(await saveMealPreset(index + 1, suggestion.name, suggestion.meal_type, suggestion.items));
  }
  return presets;
}

export async function saveMealPreset(
  slot: number,
  name: string,
  mealType: MealType,
  items: string[]
): Promise<MealPreset> {
  if (!Number.isInteger(slot) || slot < 1 || slot > 10) {
    throw new Error("Il numero del gruppo deve essere compreso tra 1 e 10.");
  }
  const normalizedItems = [...new Set(items.map(normalizeName).filter(Boolean))];
  if (!normalizedItems.length) {
    throw new Error("Inserisci almeno una portata.");
  }
  const id = `meal-preset-${slot}`;
  const existing = await db.meal_presets.get(id);
  const timestamp = nowIso();
  const preset: MealPreset = existing
    ? {
        ...existing,
        name: normalizeName(name),
        meal_type: mealType,
        items: normalizedItems,
        deleted_at: null,
        updated_at: timestamp,
        sync_status: "pending"
      }
    : {
        ...makeBase(),
        id,
        slot,
        name: normalizeName(name),
        meal_type: mealType,
        items: normalizedItems
      };
  await saveEntity("meal_presets", preset);
  return preset;
}

export async function deleteMealPreset(preset: MealPreset): Promise<MealPreset> {
  const timestamp = nowIso();
  const deleted: MealPreset = {
    ...preset,
    deleted_at: timestamp,
    updated_at: timestamp,
    sync_status: "pending"
  };
  await saveEntity("meal_presets", deleted, "delete");
  return deleted;
}

export async function logFoodGroup(items: string[], meal: FoodMealTarget = {}): Promise<FoodLog[]> {
  let target = meal;
  const logs: FoodLog[] = [];
  for (const item of items) {
    const log = await logFood(item, "", target);
    if (!log) {
      continue;
    }
    logs.push(log);
    target = {
      id: log.meal_id ?? target.id,
      eaten_at: log.eaten_at,
      meal_type: log.meal_type,
      notes: log.meal_notes
    };
  }
  return logs;
}

export async function logFood(name: string, notes = "", meal: FoodMealTarget = {}): Promise<FoodLog | undefined> {
  const normalized = normalizeName(name);
  if (!normalized) {
    return;
  }
  const timestamp = nowIso();
  const eatenAt = meal.eaten_at ?? timestamp;
  if (Number.isNaN(new Date(eatenAt).getTime())) {
    throw new Error("Data o ora del pasto non valida.");
  }
  const existing = await db.food_items
    .where("name")
    .equalsIgnoreCase(normalized)
    .filter((item) => !item.deleted_at)
    .first();
  const item: FoodItem = existing
    ? {
        ...existing,
        use_count: existing.use_count + 1,
        last_used_at: !existing.last_used_at || eatenAt > existing.last_used_at ? eatenAt : existing.last_used_at,
        updated_at: timestamp,
        sync_status: "pending"
      }
    : {
        ...makeBase(),
        name: normalized,
        use_count: 1,
        last_used_at: eatenAt
      };

  const log: FoodLog = {
    ...makeBase(),
    food_item_id: item.id,
    meal_id: meal.id ?? createId(),
    meal_type: meal.meal_type ?? inferMealType(eatenAt),
    meal_notes: meal.notes?.trim() ?? "",
    eaten_at: eatenAt,
    notes: notes.trim()
  };
  await db.transaction("rw", db.food_items, db.food_logs, db.sync_mutations, async () => {
    await saveEntity("food_items", item);
    await saveEntity("food_logs", log);
  });
  return log;
}

export async function updateFoodMeal(
  mealId: string,
  eatenAt: string,
  mealType: MealType,
  notes: string
): Promise<FoodLog[]> {
  if (Number.isNaN(new Date(eatenAt).getTime())) {
    throw new Error("Data o ora del pasto non valida.");
  }
  const logs = active(await db.food_logs.toArray()).filter((log) => effectiveMealId(log) === mealId);
  if (!logs.length) {
    throw new Error("Pasto non trovato.");
  }

  const timestamp = nowIso();
  const updated = logs.map((log) => ({
    ...log,
    meal_id: mealId,
    meal_type: mealType,
    meal_notes: notes.trim(),
    eaten_at: eatenAt,
    updated_at: timestamp,
    sync_status: "pending" as const
  }));

  await db.transaction("rw", db.food_logs, db.food_items, db.sync_mutations, async () => {
    for (const log of updated) {
      await saveEntity("food_logs", log);
    }
    for (const itemId of new Set(updated.map((log) => log.food_item_id))) {
      await refreshFoodItemStats(itemId, timestamp);
    }
  });
  return updated;
}

export async function deleteFoodMeal(mealId: string): Promise<void> {
  const logs = active(await db.food_logs.toArray()).filter((log) => effectiveMealId(log) === mealId);
  if (!logs.length) {
    throw new Error("Pasto non trovato.");
  }
  await db.transaction("rw", db.food_logs, db.food_items, db.sync_mutations, async () => {
    for (const log of logs) {
      await deleteFoodLog(log);
    }
  });
}

export async function updateFoodLog(
  foodLog: FoodLog,
  foodName: string,
  eatenAt: string,
  notes: string
): Promise<FoodLog> {
  const normalized = normalizeName(foodName);
  if (!normalized) {
    throw new Error("Il nome del cibo non puo essere vuoto.");
  }
  if (Number.isNaN(new Date(eatenAt).getTime())) {
    throw new Error("Data o ora non valida.");
  }
  const current = await db.food_logs.get(foodLog.id);
  if (!current || current.deleted_at) {
    throw new Error("Pasto non trovato.");
  }

  const timestamp = nowIso();
  const oldItem = await db.food_items.get(current.food_item_id);
  let targetItem = await db.food_items
    .where("name")
    .equalsIgnoreCase(normalized)
    .filter((item) => !item.deleted_at)
    .first();

  if (!targetItem) {
    targetItem = {
      ...makeBase(),
      name: normalized,
      use_count: 0,
      last_used_at: eatenAt
    };
  }

  if (targetItem.id !== current.food_item_id) {
    const updatedTarget: FoodItem = {
      ...targetItem,
      use_count: targetItem.use_count + 1,
      last_used_at:
        !targetItem.last_used_at || eatenAt > targetItem.last_used_at ? eatenAt : targetItem.last_used_at,
      updated_at: timestamp,
      sync_status: "pending"
    };
    await saveEntity("food_items", updatedTarget);

    if (oldItem && !oldItem.deleted_at) {
      const remaining = await db.food_logs
        .where("food_item_id")
        .equals(oldItem.id)
        .filter((log) => !log.deleted_at && log.id !== current.id)
        .toArray();
      if (remaining.length === 0) {
        await saveEntity(
          "food_items",
          { ...oldItem, deleted_at: timestamp, updated_at: timestamp, sync_status: "pending" },
          "delete"
        );
      } else {
        const lastUsedAt = remaining
          .map((log) => log.eaten_at)
          .sort((a, b) => b.localeCompare(a))[0];
        await saveEntity("food_items", {
          ...oldItem,
          use_count: Math.max(1, oldItem.use_count - 1),
          last_used_at: lastUsedAt,
          updated_at: timestamp,
          sync_status: "pending"
        });
      }
    }
    targetItem = updatedTarget;
  } else {
    await saveEntity("food_items", {
      ...targetItem,
      name: normalized,
      last_used_at:
        !targetItem.last_used_at || eatenAt > targetItem.last_used_at ? eatenAt : targetItem.last_used_at,
      updated_at: timestamp,
      sync_status: "pending"
    });
  }

  const updated: FoodLog = {
    ...current,
    food_item_id: targetItem.id,
    eaten_at: eatenAt,
    notes: notes.trim(),
    updated_at: timestamp,
    sync_status: "pending"
  };
  await saveEntity("food_logs", updated);
  return updated;
}

export async function deleteFoodLog(foodLog: FoodLog): Promise<FoodLog> {
  const current = await db.food_logs.get(foodLog.id);
  if (!current || current.deleted_at) {
    throw new Error("Pasto non trovato.");
  }
  const timestamp = nowIso();
  const deleted: FoodLog = {
    ...current,
    deleted_at: timestamp,
    updated_at: timestamp,
    sync_status: "pending"
  };
  await saveEntity("food_logs", deleted, "delete");
  await refreshFoodItemStats(current.food_item_id, timestamp);
  return deleted;
}

async function refreshFoodItemStats(itemId: string, timestamp: string): Promise<void> {
  const item = await db.food_items.get(itemId);
  if (!item || item.deleted_at) {
    return;
  }
  const remaining = await db.food_logs
    .where("food_item_id")
    .equals(item.id)
    .filter((log) => !log.deleted_at)
    .toArray();
  if (!remaining.length) {
    await saveEntity(
      "food_items",
      { ...item, deleted_at: timestamp, updated_at: timestamp, sync_status: "pending" },
      "delete"
    );
    return;
  }
  await saveEntity("food_items", {
    ...item,
    use_count: remaining.length,
    last_used_at: remaining.map((log) => log.eaten_at).sort((a, b) => b.localeCompare(a))[0],
    updated_at: timestamp,
    sync_status: "pending"
  });
}

export function medicationDoseCount(log: MedicationLog): number {
  if (typeof log.dose_count === "number" && Number.isFinite(log.dose_count)) {
    return Math.max(0, Math.round(log.dose_count));
  }
  return log.taken ? 1 : 0;
}

function validateMedicationDoseCount(doseCount: number): number {
  if (!Number.isInteger(doseCount) || doseCount < 0 || doseCount > 9) {
    throw new Error("Il numero di dosi deve essere compreso tra 0 e 9.");
  }
  return doseCount;
}

export async function setMedicationDose(
  date: string,
  kind: MedicationKind,
  doseCount: number
): Promise<MedicationLog> {
  const normalizedDoseCount = validateMedicationDoseCount(doseCount);
  const taken = normalizedDoseCount > 0;
  const existing = await db.medication_logs
    .where("date")
    .equals(date)
    .filter((log) => !log.deleted_at && normalizeMedicationKind(log.medication) === kind)
    .first();
  const log: MedicationLog = existing
    ? {
        ...existing,
        medication: kind,
        taken,
        dose_count: normalizedDoseCount,
        taken_at: taken ? existing.taken_at ?? nowIso() : null,
        updated_at: nowIso(),
        sync_status: "pending"
      }
    : {
        ...makeBase(),
        date,
        medication: kind,
        taken,
        dose_count: normalizedDoseCount,
        taken_at: taken ? nowIso() : null
      };
  await saveEntity("medication_logs", log);
  return log;
}

export async function updateMedicationLog(
  medicationLog: MedicationLog,
  date: string,
  kind: MedicationKind,
  doseCount: number,
  takenAt?: string | null
): Promise<MedicationLog> {
  const normalizedDoseCount = validateMedicationDoseCount(doseCount);
  const taken = normalizedDoseCount > 0;
  if (!isDateKey(date)) {
    throw new Error("Data non valida.");
  }
  const current = await db.medication_logs.get(medicationLog.id);
  if (!current || current.deleted_at) {
    throw new Error("Registrazione farmaco non trovata.");
  }
  const duplicate = await db.medication_logs
    .where("date")
    .equals(date)
    .filter(
      (log) =>
        !log.deleted_at &&
        log.id !== current.id &&
        normalizeMedicationKind(log.medication) === kind
    )
    .first();
  if (duplicate) {
    throw new Error(`Esiste gia una registrazione di ${MEDICATION_LABELS[kind]} per questa data.`);
  }
  if (taken && (!takenAt || Number.isNaN(new Date(takenAt).getTime()))) {
    throw new Error("Ora di assunzione non valida.");
  }

  const updated: MedicationLog = {
    ...current,
    date,
    medication: kind,
    taken,
    dose_count: normalizedDoseCount,
    taken_at: taken ? takenAt : null,
    updated_at: nowIso(),
    sync_status: "pending"
  };
  await saveEntity("medication_logs", updated);
  return updated;
}

export async function deleteMedicationLog(medicationLog: MedicationLog): Promise<MedicationLog> {
  const current = await db.medication_logs.get(medicationLog.id);
  if (!current || current.deleted_at) {
    throw new Error("Registrazione farmaco non trovata.");
  }
  const timestamp = nowIso();
  const deleted: MedicationLog = {
    ...current,
    deleted_at: timestamp,
    updated_at: timestamp,
    sync_status: "pending"
  };
  await saveEntity("medication_logs", deleted, "delete");
  return deleted;
}

export interface HealthDaySummary {
  date: string;
  medication_recorded: boolean;
  ipp_doses: number;
  doses_by_kind: Record<MedicationKind, number>;
  vomit_events: number;
  symptoms: Partial<Record<SymptomKind, number>>;
}

export function summarizeHealthDays(
  medicationLogs: MedicationLog[],
  symptomLogs: SymptomLog[]
): HealthDaySummary[] {
  const days = new Map<string, HealthDaySummary>();
  const ensureDay = (date: string) => {
    const existing = days.get(date);
    if (existing) {
      return existing;
    }
    const created: HealthDaySummary = {
      date,
      medication_recorded: false,
      ipp_doses: 0,
      doses_by_kind: { ipp: 0, antiacido: 0, brioschi: 0 },
      vomit_events: 0,
      symptoms: {}
    };
    days.set(date, created);
    return created;
  };

  for (const log of active(medicationLogs)) {
    const kind = normalizeMedicationKind(log.medication);
    const doses = medicationDoseCount(log);
    const day = ensureDay(log.date);
    day.doses_by_kind[kind] += doses;
    if (kind === "ipp") {
      day.medication_recorded = true;
      day.ipp_doses = doses;
    }
  }
  for (const log of active(symptomLogs)) {
    const day = ensureDay(localDateKey(log.occurred_at));
    if (log.vomited || log.symptom === "episodio_gastrico") {
      day.vomit_events += 1;
    }
    if (log.symptom) {
      day.symptoms[log.symptom] = (day.symptoms[log.symptom] ?? 0) + 1;
    }
  }

  return [...days.values()].sort((a, b) => a.date.localeCompare(b.date));
}

export async function logSymptom(symptom: SymptomKind, notes = ""): Promise<SymptomLog> {
  const entry: SymptomLog = {
    ...makeBase(),
    occurred_at: nowIso(),
    felt_sick: true,
    vomited: symptom === "episodio_gastrico",
    symptom,
    notes
  };
  await saveEntity("symptom_logs", entry);
  return entry;
}

export async function updateSymptomLog(
  symptomLog: SymptomLog,
  updates: Pick<SymptomLog, "occurred_at" | "notes"> & { symptom: SymptomKind | null }
): Promise<SymptomLog> {
  if (Number.isNaN(new Date(updates.occurred_at).getTime())) {
    throw new Error("Data o ora non valida.");
  }
  const current = await db.symptom_logs.get(symptomLog.id);
  if (!current || current.deleted_at) {
    throw new Error("Sintomo non trovato.");
  }
  const updated: SymptomLog = {
    ...current,
    occurred_at: updates.occurred_at,
    symptom: updates.symptom,
    felt_sick: true,
    vomited: updates.symptom === "episodio_gastrico",
    notes: updates.notes.trim(),
    updated_at: nowIso(),
    sync_status: "pending"
  };
  await saveEntity("symptom_logs", updated);
  return updated;
}

export async function deleteSymptomLog(symptomLog: SymptomLog): Promise<SymptomLog> {
  const current = await db.symptom_logs.get(symptomLog.id);
  if (!current || current.deleted_at) {
    throw new Error("Sintomo non trovato.");
  }
  const timestamp = nowIso();
  const deleted: SymptomLog = {
    ...current,
    deleted_at: timestamp,
    updated_at: timestamp,
    sync_status: "pending"
  };
  await saveEntity("symptom_logs", deleted, "delete");
  return deleted;
}

export async function saveVoiceNote(audioBlob: Blob, mimeType: string, durationSeconds: number): Promise<VoiceNote> {
  const timestamp = nowIso();
  const note: VoiceNote = {
    ...makeBase(),
    created_at: timestamp,
    title: `Nota ${formatDateTime(timestamp)}`,
    audio_blob: audioBlob,
    mime_type: mimeType,
    duration_seconds: durationSeconds,
    upload_status: "pending",
    transcription_status: "queued",
    transcription_job_id: null,
    transcript: null,
    notes: ""
  };
  await db.voice_notes.put(note);
  await queueMutation("voice_notes", note.id, "upsert", { ...note, audio_blob: undefined });
  emitDbChange();
  return note;
}

export async function updateVoiceNote(
  voiceNote: VoiceNote,
  updates: Pick<VoiceNote, "title" | "notes" | "transcript">
): Promise<VoiceNote> {
  const current = await db.voice_notes.get(voiceNote.id);
  if (!current || current.deleted_at) {
    throw new Error("Nota vocale non trovata.");
  }
  const title = normalizeName(updates.title);
  if (!title) {
    throw new Error("Il titolo non puo essere vuoto.");
  }
  const transcript = updates.transcript?.trim() || null;
  const updated: VoiceNote = {
    ...current,
    title,
    notes: updates.notes.trim(),
    transcript,
    transcription_status: transcript ? "done" : current.transcription_status,
    updated_at: nowIso(),
    sync_status: "pending"
  };
  await saveEntity("voice_notes", updated);
  return updated;
}

export async function deleteVoiceNote(voiceNote: VoiceNote): Promise<VoiceNote> {
  const current = await db.voice_notes.get(voiceNote.id);
  if (!current || current.deleted_at) {
    throw new Error("Nota vocale non trovata.");
  }
  const timestamp = nowIso();
  const deleted: VoiceNote = {
    ...current,
    audio_blob: undefined,
    deleted_at: timestamp,
    updated_at: timestamp,
    sync_status: "pending"
  };
  await saveEntity("voice_notes", deleted, "delete");
  return deleted;
}

export function rankFoodItems(items: FoodItem[], limit = 12): FoodItem[] {
  const now = Date.now();
  return active(items)
    .slice()
    .sort((a, b) => scoreFood(b, now) - scoreFood(a, now) || a.name.localeCompare(b.name))
    .slice(0, limit);
}

export function searchFoodItems(items: FoodItem[], query: string, limit = 6): FoodItem[] {
  const normalizedQuery = normalizeFoodSearch(query);
  if (normalizedQuery.length < 2) {
    return [];
  }

  const terms = normalizedQuery.split(" ");
  const now = Date.now();
  return active(items)
    .map((item) => {
      const normalizedName = normalizeFoodSearch(item.name);
      if (!terms.every((term) => normalizedName.includes(term))) {
        return null;
      }
      const words = normalizedName.split(" ");
      const matchRank =
        normalizedName === normalizedQuery
          ? 0
          : normalizedName.startsWith(normalizedQuery)
            ? 1
            : terms.every((term) => words.some((word) => word.startsWith(term)))
              ? 2
              : 3;
      const queryIndex = normalizedName.indexOf(normalizedQuery);
      return { item, matchRank, matchIndex: queryIndex >= 0 ? queryIndex : Number.MAX_SAFE_INTEGER };
    })
    .filter((match): match is { item: FoodItem; matchRank: number; matchIndex: number } => match !== null)
    .sort(
      (a, b) =>
        a.matchRank - b.matchRank ||
        a.matchIndex - b.matchIndex ||
        scoreFood(b.item, now) - scoreFood(a.item, now) ||
        a.item.name.localeCompare(b.item.name)
    )
    .slice(0, limit)
    .map(({ item }) => item);
}

export function rankProjects(projects: Project[], sessions: WorkSession[], clientId: string, limit = 8): Project[] {
  const usage = new Map<string, { count: number; last: number }>();
  for (const session of active(sessions)) {
    if (session.client_id !== clientId) {
      continue;
    }
    const item = usage.get(session.project_id) ?? { count: 0, last: 0 };
    item.count += 1;
    item.last = Math.max(item.last, new Date(session.started_at).getTime());
    usage.set(session.project_id, item);
  }

  return active(projects)
    .filter((project) => project.client_id === clientId && !project.archived)
    .sort((a, b) => {
      const aUsage = usage.get(a.id) ?? { count: 0, last: 0 };
      const bUsage = usage.get(b.id) ?? { count: 0, last: 0 };
      return bUsage.count * 1000 + bUsage.last / 1_000_000 - (aUsage.count * 1000 + aUsage.last / 1_000_000);
    })
    .slice(0, limit);
}

export function rankRecentProjects(projects: Project[], sessions: WorkSession[], limit = 5): Project[] {
  const projectMap = new Map(
    active(projects)
      .filter((project) => !project.archived)
      .map((project) => [project.id, project])
  );
  const seen = new Set<string>();
  const result: Project[] = [];

  for (const session of active(sessions)
    .slice()
    .sort((a, b) => b.started_at.localeCompare(a.started_at))) {
    const project = projectMap.get(session.project_id);
    if (!project || seen.has(project.id)) {
      continue;
    }
    seen.add(project.id);
    result.push(project);
    if (result.length === limit) {
      break;
    }
  }
  return result;
}

export function rankFrequentProjects(projects: Project[], sessions: WorkSession[], limit = 3): Project[] {
  const usage = new Map<string, { count: number; last: number }>();
  for (const session of active(sessions)) {
    const item = usage.get(session.project_id) ?? { count: 0, last: 0 };
    item.count += 1;
    item.last = Math.max(item.last, new Date(session.started_at).getTime());
    usage.set(session.project_id, item);
  }

  return active(projects)
    .filter((project) => !project.archived && usage.has(project.id))
    .sort((a, b) => {
      const aUsage = usage.get(a.id) ?? { count: 0, last: 0 };
      const bUsage = usage.get(b.id) ?? { count: 0, last: 0 };
      return bUsage.count - aUsage.count || bUsage.last - aUsage.last || a.name.localeCompare(b.name);
    })
    .slice(0, limit);
}

export interface WorkGap {
  started_at: string;
  ended_at: string;
  minutes: number;
}

export interface WorkDaySummary {
  date: string;
  first_started_at: string;
  last_ended_at: string;
  worked_minutes: number;
  covered_minutes: number;
  pause_minutes: number;
  overlap_minutes: number;
  gaps: WorkGap[];
  sessions: WorkSession[];
}

export function summarizeWorkDay(sessions: WorkSession[]): WorkDaySummary | undefined {
  const completed = active(sessions)
    .filter((session): session is WorkSession & { ended_at: string } => Boolean(session.ended_at))
    .slice()
    .sort((a, b) => a.started_at.localeCompare(b.started_at));
  if (!completed.length) {
    return undefined;
  }

  const workedMinutes = completed.reduce(
    (total, session) => total + minutesBetween(session.started_at, session.ended_at),
    0
  );
  const firstStartedAt = completed[0].started_at;
  let mergedEnd = completed[0].ended_at;
  let coveredMinutes = minutesBetween(firstStartedAt, mergedEnd);
  const gaps: WorkGap[] = [];

  for (const session of completed.slice(1)) {
    const startTime = new Date(session.started_at).getTime();
    const mergedEndTime = new Date(mergedEnd).getTime();
    if (startTime > mergedEndTime) {
      gaps.push({
        started_at: mergedEnd,
        ended_at: session.started_at,
        minutes: minutesBetween(mergedEnd, session.started_at)
      });
      coveredMinutes += minutesBetween(session.started_at, session.ended_at);
      mergedEnd = session.ended_at;
      continue;
    }
    if (new Date(session.ended_at).getTime() > mergedEndTime) {
      coveredMinutes += minutesBetween(mergedEnd, session.ended_at);
      mergedEnd = session.ended_at;
    }
  }

  const lastEndedAt = completed.reduce(
    (latest, session) => (session.ended_at.localeCompare(latest) > 0 ? session.ended_at : latest),
    completed[0].ended_at
  );
  const spanMinutes = minutesBetween(firstStartedAt, lastEndedAt);

  return {
    date: localDateKey(firstStartedAt),
    first_started_at: firstStartedAt,
    last_ended_at: lastEndedAt,
    worked_minutes: workedMinutes,
    covered_minutes: coveredMinutes,
    pause_minutes: Math.max(0, spanMinutes - coveredMinutes),
    overlap_minutes: Math.max(0, workedMinutes - coveredMinutes),
    gaps,
    sessions: completed
  };
}

export function localDateKey(value: string | Date): string {
  return todayKey(typeof value === "string" ? new Date(value) : value);
}

export function matchLocationRule(rules: LocationRule[], coordinates: Coordinates): LocationRule | undefined {
  return active(rules)
    .map((rule) => ({ rule, distance: distanceMeters(rule, coordinates) }))
    .filter(({ rule, distance }) => distance <= rule.radius_meters)
    .sort((a, b) => a.distance - b.distance)[0]?.rule;
}

export async function saveLocationRule(
  clientId: string,
  label: string,
  coordinates: Coordinates,
  radiusMeters = 120
): Promise<LocationRule> {
  const rule: LocationRule = {
    ...makeBase(),
    client_id: clientId,
    label: normalizeName(label || "Sede"),
    latitude: coordinates.latitude,
    longitude: coordinates.longitude,
    radius_meters: radiusMeters,
    last_confirmed_at: nowIso()
  };
  await saveEntity("location_rules", rule);
  return rule;
}

export function buildInvoiceSummaries(
  clients: Client[],
  sessions: WorkSession[],
  fromDate: string,
  toDate: string,
  clientId = ""
): InvoiceSummary[] {
  const clientMap = new Map(clients.map((client) => [client.id, client]));
  const grouped = new Map<string, InvoiceSummary>();
  const from = new Date(`${fromDate}T00:00:00.000`).getTime();
  const to = new Date(`${toDate}T23:59:59.999`).getTime();

  for (const session of active(sessions)) {
    if (!session.billable || !session.ended_at || (clientId && session.client_id !== clientId)) {
      continue;
    }
    const started = new Date(session.started_at).getTime();
    if (Number.isNaN(started) || started < from || started > to) {
      continue;
    }
    const client = clientMap.get(session.client_id);
    if (!client) {
      continue;
    }
    const exactMinutes = minutesBetween(session.started_at, session.ended_at);
    const increment = client.billing_increment_minutes || 15;
    const billableMinutes = roundMinutes(exactMinutes, increment);
    const rate = session.hourly_rate_cents || client.hourly_rate_cents;
    const amount = Math.round((billableMinutes / 60) * rate);
    const summary =
      grouped.get(client.id) ??
      ({
        client,
        lines: [],
        exact_minutes: 0,
        billable_minutes: 0,
        amount_cents: 0
      } satisfies InvoiceSummary);
    summary.lines.push({
      session,
      exact_minutes: exactMinutes,
      billable_minutes: billableMinutes,
      amount_cents: amount
    });
    summary.exact_minutes += exactMinutes;
    summary.billable_minutes += billableMinutes;
    summary.amount_cents += amount;
    grouped.set(client.id, summary);
  }

  return [...grouped.values()].sort((a, b) => a.client.name.localeCompare(b.client.name));
}

export function invoiceToCsv(summaries: InvoiceSummary[], projectNameFor: (projectId: string) => string): string {
  const rows = [["cliente", "progetto", "inizio", "fine", "minuti_esatti", "minuti_fatturabili", "importo", "note"]];
  for (const summary of summaries) {
    for (const line of summary.lines) {
      rows.push([
        summary.client.name,
        projectNameFor(line.session.project_id),
        line.session.started_at,
        line.session.ended_at ?? "",
        String(line.exact_minutes),
        String(line.billable_minutes),
        formatMoney(line.amount_cents, summary.client.currency),
        line.session.notes
      ]);
    }
  }
  return rows.map((row) => row.map(csvCell).join(",")).join("\n");
}

export function roundMinutes(minutes: number, increment: number): number {
  if (minutes <= 0) {
    return 0;
  }
  return Math.ceil(minutes / increment) * increment;
}

export function minutesBetween(startIso: string, endIso: string): number {
  const elapsed = new Date(endIso).getTime() - new Date(startIso).getTime();
  if (elapsed <= 0) {
    return 0;
  }
  return Math.max(1, Math.round(elapsed / 60_000));
}

export function roundSessionEndToMinute(startIso: string, endIso: string): string {
  const start = new Date(startIso).getTime();
  const end = new Date(endIso).getTime();
  const elapsedMinutes = Math.max(1, Math.ceil((end - start) / 60_000));
  return new Date(start + elapsedMinutes * 60_000).toISOString();
}

export function formatDuration(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (!hours) {
    return `${mins} min`;
  }
  return `${hours}h ${String(mins).padStart(2, "0")}m`;
}

export function formatMoney(cents: number, currency = "EUR"): string {
  return new Intl.NumberFormat("it-IT", { style: "currency", currency }).format(cents / 100);
}

export function formatDateTime(iso: string): string {
  return new Intl.DateTimeFormat("it-IT", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit"
  }).format(new Date(iso));
}

export function getTodayMedication(
  logs: MedicationLog[],
  kind: MedicationKind
): MedicationLog | undefined {
  const date = todayKey();
  return active(logs).find(
    (log) => normalizeMedicationKind(log.medication) === kind && log.date === date
  );
}

function normalizeName(name: string): string {
  return name.trim().replace(/\s+/g, " ");
}

function normalizeFoodSearch(value: string): string {
  return normalizeName(value)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLocaleLowerCase("it");
}

function combinations<T>(items: T[], size: number, start = 0, prefix: T[] = []): T[][] {
  if (prefix.length === size) {
    return [prefix];
  }
  const result: T[][] = [];
  for (let index = start; index <= items.length - (size - prefix.length); index += 1) {
    result.push(...combinations(items, size, index + 1, [...prefix, items[index]]));
  }
  return result;
}

function setSimilarity(left: string[], right: string[]): number {
  const leftSet = new Set(left.map((item) => item.toLocaleLowerCase("it-IT")));
  const rightSet = new Set(right.map((item) => item.toLocaleLowerCase("it-IT")));
  const intersection = [...leftSet].filter((item) => rightSet.has(item)).length;
  return intersection / (leftSet.size + rightSet.size - intersection);
}

async function billingForSession(
  client: Client,
  startedAt: string
): Promise<{ started_at: string; hourly_rate_cents: number; currency: string }> {
  const rates = await db.client_rates.where("client_id").equals(client.id).toArray();
  const rate = effectiveClientRate(rates, client.id, startedAt);
  return {
    started_at: startedAt,
    hourly_rate_cents: rate?.hourly_rate_cents ?? client.hourly_rate_cents,
    currency: rate?.currency ?? client.currency
  };
}

function isDateKey(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return false;
  }
  const date = new Date(`${value}T12:00:00`);
  return !Number.isNaN(date.getTime()) && todayKey(date) === value;
}

function scoreFood(item: FoodItem, now: number): number {
  const lastUsed = item.last_used_at ? new Date(item.last_used_at).getTime() : 0;
  const daysSince = lastUsed ? Math.max(0, (now - lastUsed) / 86_400_000) : 365;
  return item.use_count * 10 + Math.max(0, 30 - daysSince);
}

function distanceMeters(a: Coordinates, b: Coordinates): number {
  const radius = 6_371_000;
  const phi1 = toRad(a.latitude);
  const phi2 = toRad(b.latitude);
  const deltaPhi = toRad(b.latitude - a.latitude);
  const deltaLambda = toRad(b.longitude - a.longitude);
  const h =
    Math.sin(deltaPhi / 2) * Math.sin(deltaPhi / 2) +
    Math.cos(phi1) * Math.cos(phi2) * Math.sin(deltaLambda / 2) * Math.sin(deltaLambda / 2);
  return radius * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
}

function toRad(value: number): number {
  return (value * Math.PI) / 180;
}

function csvCell(value: string): string {
  return `"${value.replace(/"/g, '""')}"`;
}

// --- weekly carryover (tetto virtuale 50h a settimana, solo di esibizione) ---
// Le ore oltre il tetto non spariscono: entrano nel conteggio della settimana
// lavorata successiva (debito a catena). NON toccano ore fatturate/export.
export const WEEKLY_COUNT_CAP_MINUTES = 50 * 60;

export interface WeeklyCarryWeek {
  actual_minutes: number;
  counted_minutes: number;
  carry_in_minutes: number;
  carry_out_minutes: number;
}

export interface WeeklyCarryPlan {
  cap_minutes: number;
  weeks: Map<string, WeeklyCarryWeek>;
  sessions: Map<string, { counted_minutes: number; carried_minutes: number }>;
  pending_carry_minutes: number;
}

export function weekStartKey(dateKey: string): string {
  const [y, m, d] = dateKey.split("-").map(Number);
  const date = new Date(y, m - 1, d);
  const weekday = date.getDay() === 0 ? 7 : date.getDay();
  date.setDate(date.getDate() - (weekday - 1));
  return localDateKey(date);
}

export function planWeeklyCarryover(
  sessions: WorkSession[],
  capMinutes: number = WEEKLY_COUNT_CAP_MINUTES
): WeeklyCarryPlan {
  const byWeek = new Map<string, WorkSession[]>();
  for (const session of active(sessions)) {
    if (!session.ended_at) continue;
    const key = weekStartKey(localDateKey(session.started_at));
    const bucket = byWeek.get(key);
    if (bucket) bucket.push(session);
    else byWeek.set(key, [session]);
  }

  const weeks = new Map<string, WeeklyCarryWeek>();
  const sessionPlan = new Map<string, { counted_minutes: number; carried_minutes: number }>();
  let carryIn = 0;

  for (const key of [...byWeek.keys()].sort()) {
    const rows = byWeek
      .get(key)!
      .slice()
      .sort((a, b) => a.started_at.localeCompare(b.started_at));
    let actual = 0;
    let carryOut = 0;
    let load = carryIn;

    const autoRows: Array<{ session: WorkSession; minutes: number }> = [];
    for (const row of rows) {
      const minutes = minutesBetween(row.started_at, row.ended_at!);
      actual += minutes;
      if (row.carry_override === "keep") {
        sessionPlan.set(row.id, { counted_minutes: minutes, carried_minutes: 0 });
        load += minutes;
      } else if (row.carry_override === "carry") {
        sessionPlan.set(row.id, { counted_minutes: 0, carried_minutes: minutes });
        carryOut += minutes;
      } else {
        autoRows.push({ session: row, minutes });
      }
    }
    for (const row of autoRows) {
      const counted = Math.min(row.minutes, Math.max(0, capMinutes - load));
      const carried = row.minutes - counted;
      load += counted;
      carryOut += carried;
      sessionPlan.set(row.session.id, { counted_minutes: counted, carried_minutes: carried });
    }

    weeks.set(key, {
      actual_minutes: actual,
      counted_minutes: load,
      carry_in_minutes: carryIn,
      carry_out_minutes: carryOut
    });
    carryIn = carryOut;
  }

  return { cap_minutes: capMinutes, weeks, sessions: sessionPlan, pending_carry_minutes: carryIn };
}
